import React, { useState, useEffect, useRef } from "react";
import { Drawer, List, Avatar, Badge, Input, Button, Spin, Space } from "antd";
import { MessageOutlined, ArrowLeftOutlined, SendOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSanctum } from "react-sanctum";
import apiClient from "../../services/api";
import moment from "moment";
import "./ChatDrawer.css";

const ChatDrawer = () => {
  const { user } = useSanctum();
  const queryClient = useQueryClient();
  const [visible, setVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messageText, setMessageText] = useState("");
  const messagesEndRef = useRef(null);

  // Unread Count (Global)
  const { data: unreadData } = useQuery(
    "chat_unread",
    async () => {
      if (user) {
        const response = await apiClient.get("/chat/unread");
        return response.data;
      }
      return { count: 0 };
    },
    { enabled: !!user, refetchInterval: 30000 }
  );

  // Users List with unread count
  const { data: usersData, isLoading: isLoadingUsers } = useQuery(
    "chat_users",
    async () => {
      const response = await apiClient.get("/chat/users");
      return response.data || [];
    },
    { enabled: !!user && visible && !selectedUser, refetchInterval: 10000 }
  );

  // Messages with selected user
  const { data: messagesData, isLoading: isLoadingMessages } = useQuery(
    ["chat_messages", selectedUser?.id],
    async () => {
      const response = await apiClient.get(`/chat/messages/${selectedUser.id}`);
      return response.data || [];
    },
    { enabled: !!selectedUser, refetchInterval: 3000 }
  );

  const sendMessageMutation = useMutation(
    (message) => apiClient.post(`/chat/messages/${selectedUser.id}`, { message }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["chat_messages", selectedUser.id]);
        setMessageText("");
      },
    }
  );

  // Determine if the current user belongs to an 'own' company or is superadmin
  // Based on backend logic, if the API returns users, they can use chat.
  // We check if it's rendered by simply returning null if usersData API returns empty array AND it's not loading ?
  // Actually, wait, let's just render the icon always and let the backend manage who gets an empty list. or maybe we hide it if usersData is consistently 0.
  // We'll let it be visible for everyone for now. If they don't have access, they see an empty list.

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (visible && selectedUser && messagesData) {
      scrollToBottom();
      // Invalidate global unread so the badge updates
      queryClient.invalidateQueries("chat_unread");
    }
  }, [messagesData, visible, selectedUser, queryClient]);

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
    setSelectedUser(null);
  };

  const handleSend = () => {
    if (messageText.trim()) {
      sendMessageMutation.mutate(messageText.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <div style={{ marginRight: "24px", cursor: "pointer", marginTop: "6px" }} onClick={showDrawer}>
        <Badge count={unreadData?.count || 0} size="small">
          <MessageOutlined style={{ fontSize: "20px", color: "inherit" }} />
        </Badge>
      </div>

      <Drawer
        title={
          selectedUser ? (
            <Space>
              <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => setSelectedUser(null)} style={{ padding: 0 }} />
              {selectedUser.name}
            </Space>
          ) : (
            "Chat Corporativo"
          )
        }
        placement="right"
        onClose={onClose}
        visible={visible}
        width={350}
        bodyStyle={{ display: "flex", flexDirection: "column", padding: 0 }}
      >
        {!selectedUser ? (
          <div style={{ flex: 1, overflowY: "auto" }}>
            <List
              loading={isLoadingUsers}
              itemLayout="horizontal"
              dataSource={usersData || []}
              renderItem={(item) => (
                <List.Item
                  style={{ cursor: "pointer", padding: "12px 24px" }}
                  className="chat-user-item"
                  onClick={() => setSelectedUser(item)}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge count={item.unread_count}>
                        <Avatar src={`https://avatars.dicebear.com/api/initials/${item.name}.svg`} />
                      </Badge>
                    }
                    title={item.name}
                  />
                </List.Item>
              )}
            />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px", background: "#f0f2f5" }}>
              {isLoadingMessages ? (
                <div style={{ textAlign: "center", marginTop: "20px" }}><Spin /></div>
              ) : (
                messagesData?.map((msg) => {
                  const isMine = msg.sender_id === user.id;
                  return (
                    <div
                      key={msg.id}
                      style={{
                        display: "flex",
                        justifyContent: isMine ? "flex-end" : "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          maxWidth: "75%",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          background: isMine ? "#1890ff" : "#fff",
                          color: isMine ? "#fff" : "#000",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        }}
                      >
                        <div style={{ wordBreak: "break-word" }}>{msg.message}</div>
                        <div
                          style={{
                            fontSize: "10px",
                            textAlign: "right",
                            marginTop: "4px",
                            color: isMine ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.45)",
                          }}
                        >
                          {moment(msg.created_at).format("HH:mm")}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
            <div style={{ padding: "16px", background: "#fff", borderTop: "1px solid #f0f0f0" }}>
              <div style={{ display: "flex" }}>
                <Input.TextArea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Escribe un mensaje..."
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  style={{ flex: 1, marginRight: "8px" }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSend}
                  loading={sendMessageMutation.isLoading}
                />
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default ChatDrawer;
